import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AlertCircle, Check, CheckCheck, Clock, Paperclip } from "lucide-react";
import {
  useDataProvider,
  useGetMany,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import type {
  Sale,
  WhatsAppConversationState,
  WhatsAppMessage,
  WhatsAppMessageCursor,
} from "../types";
import { TimelineSkeleton } from "./TimelineSkeleton";
import { useWhatsAppClocks, WhatsAppClockBars } from "./WhatsAppClockBars";
import { WhatsAppComposer } from "./WhatsAppComposer";

const TIME_FORMAT = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/Mexico_City",
});

const StatusTick = ({ message }: { message: WhatsAppMessage }) => {
  const translate = useTranslate();
  const base = "crm.whatsapp.conversation.status";
  if (message.status === "failed") {
    return (
      <span className="flex items-center gap-1 text-destructive">
        <AlertCircle aria-hidden="true" className="h-3 w-3" />
        {translate(`${base}.failed`, { error: message.error ?? "" })}
      </span>
    );
  }
  const Icon =
    message.status === "pending"
      ? Clock
      : message.status === "sent"
        ? Check
        : CheckCheck;
  return (
    <span className="flex items-center gap-1">
      <Icon aria-hidden="true" className="h-3 w-3" />
      {translate(`${base}.${message.status}`)}
    </span>
  );
};

export const MessageBubble = ({
  message,
  senderName,
}: {
  message: WhatsAppMessage;
  senderName: string;
}) => {
  const translate = useTranslate();
  const outbound = message.direction === "out";
  return (
    <li
      data-testid="whatsapp-message"
      data-origin={message.origin}
      className={cn(
        "flex flex-col gap-1",
        outbound ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3 py-2 text-sm",
          outbound
            ? "bg-secondary text-foreground"
            : "bg-muted text-foreground",
        )}
      >
        <p className="text-xs font-semibold text-muted-foreground">
          {senderName}
        </p>
        {message.body ? (
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        ) : null}
        {!message.body && message.template_name ? (
          <p className="italic">
            {translate("crm.whatsapp.conversation.template_message", {
              name: message.template_name,
            })}
          </p>
        ) : null}
        {message.media_id || message.mime ? (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-background px-2 py-1 text-xs">
            <Paperclip aria-hidden="true" className="h-3 w-3" />
            {translate("crm.whatsapp.conversation.media", {
              mime: message.mime ?? message.type,
            })}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <time dateTime={message.message_at}>
          {TIME_FORMAT.format(new Date(message.message_at))}
        </time>
        {outbound ? <StatusTick message={message} /> : null}
      </div>
    </li>
  );
};

const saleName = (sale: Sale | undefined) =>
  sale ? `${sale.first_name} ${sale.last_name}`.trim() : "";

const ConversationBody = ({
  state,
  stateReadAt,
  dealId,
}: {
  state: WhatsAppConversationState;
  stateReadAt: number;
  dealId: Identifier;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const clocks = useWhatsAppClocks(state, stateReadAt);
  const listRef = useRef<HTMLUListElement>(null);

  const messagesQuery = useInfiniteQuery({
    queryKey: ["whatsapp", "messages", String(state.conversation_id)],
    queryFn: ({ pageParam }) =>
      dataProvider.getWhatsAppMessages(state.conversation_id, pageParam),
    initialPageParam: undefined as WhatsAppMessageCursor | undefined,
    getNextPageParam: (page): WhatsAppMessageCursor | undefined => {
      const oldest = page.messages[page.messages.length - 1];
      return page.hasMore && oldest
        ? { at: oldest.message_at, id: oldest.id }
        : undefined;
    },
  });
  // Pages arrive newest first; the timeline reads oldest first.
  const messages = (messagesQuery.data?.pages ?? [])
    .flatMap((page) => page.messages)
    .slice()
    .reverse();
  const newestId = messages[messages.length - 1]?.id;

  // Showing the timeline marks the conversation read for this user only; the unread counts of
  // the Inbox and the lead rows refresh through the shared ["whatsapp"] key. Best effort: a
  // failed mark must never break the timeline.
  const conversationId = state.conversation_id;
  useEffect(() => {
    let cancelled = false;
    dataProvider
      .markWhatsAppConversationRead(conversationId)
      .then(() => {
        if (!cancelled) {
          queryClient.invalidateQueries({ queryKey: ["whatsapp", "inbox"] });
          queryClient.invalidateQueries({
            queryKey: ["whatsapp", "leadSignals"],
          });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [conversationId, dataProvider, queryClient]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [newestId]);

  const senderIds = [
    ...new Set(
      messages
        .map((message) => message.sender_sales_id)
        .filter((id): id is Identifier => id != null),
    ),
  ];
  const { data: senders = [] } = useGetMany<Sale>(
    "sales",
    { ids: senderIds },
    { enabled: senderIds.length > 0 },
  );

  const senderName = (message: WhatsAppMessage) => {
    if (message.origin === "customer") {
      return translate("crm.whatsapp.conversation.sender.customer");
    }
    if (message.origin === "app") {
      return (
        message.sender_label ??
        translate("crm.whatsapp.conversation.sender.app")
      );
    }
    return (
      saleName(
        senders.find(
          (sale) => String(sale.id) === String(message.sender_sales_id),
        ),
      ) ||
      message.sender_label ||
      translate("crm.whatsapp.conversation.sender.advisor_fallback")
    );
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
  };

  return (
    <div className="flex flex-col gap-4">
      <WhatsAppClockBars clocks={clocks} />
      {messagesQuery.error ? (
        <div
          role="alert"
          className="flex items-center gap-3 text-sm text-destructive"
        >
          <span>{translate("crm.whatsapp.conversation.error")}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => messagesQuery.refetch()}
          >
            {translate("crm.whatsapp.conversation.retry")}
          </Button>
        </div>
      ) : messagesQuery.isPending ? (
        <TimelineSkeleton rows={3} />
      ) : messages.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.whatsapp.conversation.empty")}
        </p>
      ) : (
        <>
          {messagesQuery.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="self-center"
              disabled={messagesQuery.isFetchingNextPage}
              onClick={() => messagesQuery.fetchNextPage()}
            >
              {translate("crm.whatsapp.conversation.load_older")}
            </Button>
          ) : null}
          <ul
            ref={listRef}
            data-testid="whatsapp-timeline"
            className="flex max-h-96 flex-col gap-3 overflow-y-auto"
          >
            {messages.map((message) => (
              <MessageBubble
                key={String(message.id)}
                message={message}
                senderName={senderName(message)}
              />
            ))}
          </ul>
        </>
      )}
      <WhatsAppComposer
        key={`${dealId}`}
        conversationId={state.conversation_id}
        freeTextOpen={(clocks.freeText ?? 0) > 0}
        costKeyForCatalogue={state.template_cost_label}
        onSent={refresh}
      />
    </div>
  );
};

/**
 * Almikia Epic EW2 (TASK-079): the WhatsApp conversation of a lead, mounted on the lead file.
 * Shows the two clock bars, the message timeline (sender labels 'Cliente' / the advisor's name /
 * 'Pepe (app)', delivery ticks incl. failed with its error, media as a reference chip only,
 * paginated by conversation) and the composer. Reads go through RLS, so a user only ever sees
 * the conversations of leads she can read. A lead without a conversation shows the empty state.
 */
export const ConversationTimeline = ({ dealId }: { dealId: Identifier }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const conversation = useQuery({
    queryKey: ["whatsapp", "conversation", String(dealId)],
    queryFn: () => dataProvider.getWhatsAppConversation(dealId),
  });

  return (
    <section
      data-testid="whatsapp-conversation"
      className="flex flex-col gap-3"
    >
      <h3 className="text-sm font-semibold text-[var(--heading)]">
        {translate("crm.whatsapp.conversation.title")}
      </h3>
      {conversation.error ? (
        <div
          role="alert"
          className="flex items-center gap-3 text-sm text-destructive"
        >
          <span>{translate("crm.whatsapp.conversation.error")}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => conversation.refetch()}
          >
            {translate("crm.whatsapp.conversation.retry")}
          </Button>
        </div>
      ) : conversation.isPending ? (
        <TimelineSkeleton rows={4} />
      ) : conversation.data ? (
        <ConversationBody
          state={conversation.data}
          stateReadAt={conversation.dataUpdatedAt}
          dealId={dealId}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("crm.whatsapp.conversation.empty")}
        </p>
      )}
    </section>
  );
};
